// react
import { SyntheticEvent, useContext, useMemo, useState } from 'react';

// types
import { Contract, Document, Entity, Invoice, Pagination, Transaction } from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { useSearchParams } from 'react-router-dom';

// context
import { ContextUser } from '../../../App.context';
import { ContextUserProjectPermissions } from '../../pages/Project/Project.context';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// mui
import { Box, Grid, Paper, Typography } from '@mui/material';
import { TabContext, TabPanel } from '@mui/lab';

// ui
import { Amount } from '../../ui/Amount/Amount';
import { BtnExpand } from '../../ui/BtnExpand/BtnExpand';
import { BtnMenu } from '../../ui/BtnMenu/BtnMenu';
import { ChipStatus } from '../../ui/ChipStatus/ChipStatus';
import { IconCircle } from '../../ui/IconCircle/IconCircle';
import { MenuItemProps } from '../../ui/Menu/Menu';
import { RowKeyValue } from '../../ui/RowKeyValue/RowKeyValue';
import { TabProps, Tabs } from '../../ui/Tabs/Tabs';
import { TypographyCaption } from '../../ui/TypographyCaption/TypographyCaption';
import { UserAvatarModified } from '../../ui/UserAvatarModified/UserAvatarModified';

// tables
import { TableBasicDocs } from '../../tables/basic/TableBasicDocs/TableBasicDocs';
import { TableBasicTransactions } from '../../tables/basic/TableBasicTransactions/TableBasicTransactions';

// dialogs
import {
	DialogAddTransaction,
	DialogAddTransactionProps,
} from '../../dialogs/DialogAddTransaction/DialogAddTransaction';
import {
	DialogUpdateInvoiceStatus,
	DialogUpdateInvoiceStatusProps as DialogUpdateInvoiceStatusPropsType,
} from '../../dialogs/DialogUpdateInvoiceStatus/DialogUpdateInvoiceStatus';
import { DialogUploadDocs } from '../../dialogs/DialogUploadDocs/DialogUploadDocs';

// links
import { LinkEntity } from '../../links/LinkEntity/LinkEntity';

// icons
import {
	CheckOutlined as IconCheck,
	CloseOutlined as IconClose,
	InsertDriveFileOutlined as IconInsertDriveFile,
	PaidOutlined as IconPaid,
	UndoOutlined as IconUndo,
} from '@mui/icons-material';

// utils
import { defaultPagination } from '../../../utils/defaults';

// theme
import { theme } from '../../../theme';

export interface CardInvoiceProps {
	invoice: Invoice;
	refetchInvoices?: () => void;
}

export const CardInvoice = ({ invoice, refetchInvoices }: CardInvoiceProps) => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// hooks
	const [searchParams, setSearchParams] = useSearchParams();
	const { t } = useTranslation();

	// context
	const { user } = useContext(ContextUser);
	const { userProjectPermissions } = useContext(ContextUserProjectPermissions);

	// params
	const paramDialog = searchParams.get('dialog');
	const paramDialogInvoice = searchParams.get('dialogInvoice');

	// state
	const [documents, setDocuments] = useState<Document[]>([]);
	const [isExpanded, setIsExpanded] = useState(false);
	const [paginationDocuments, setPaginationDocuments] = useState<Pagination>(defaultPagination);
	const [tabValue, setTabValue] = useState('payments');
	const [transactions, setTransactions] = useState<Transaction[]>([]);

	// fetch payment transactions
	const { isLoading: isLoadingTransactions } = useFetch({
		isEnabled: Boolean(invoice && isExpanded && tabValue === 'payments'),
		url: `${process.env.REACT_APP_API_URL}/transactions?invoice=${invoice.id}&type=DIRECT,PAYMENT&fields=activities,amount,created,currency,date,id,modified,project,status,tax,total,type&populate=activities,activities.actor&orderBy=id&order=DESC`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setTransactions(res.data);
			}
		},
	});

	// fetch documents
	const { isLoading: isLoadingDocs, refetch: refetchDocuments } = useFetch({
		isEnabled: Boolean(invoice && isExpanded && tabValue === 'documents'),
		url: `${process.env.REACT_APP_API_URL}/documents?invoice=${invoice.id}&limit=10&page=1&fields=created,notarizationHash,id,name,type,user&populate=type,user`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setDocuments(res.data);
			}

			if (res.pagination) {
				setPaginationDocuments(res.pagination);
			}
		},
	});

	const contractNumber = useMemo(() => {
		if (typeof invoice.contract === 'object' && invoice.contract) {
			return (invoice.contract as Contract).num;
		}
		return '-';
	}, [invoice.contract]);

	const total = invoice.amount != null ? (invoice.amount ?? 0) + (invoice.tax ?? 0) : null;

	// handlers
	const handleToggleCard = () => {
		setIsExpanded(!isExpanded);
	};

	const handleChangeTab = (e: SyntheticEvent, value: string) => {
		setTabValue(value);
	};

	const handleCloseDialog = () => {
		searchParams.delete('dialog');
		searchParams.delete('dialogContract');
		searchParams.delete('dialogInvoice');
		searchParams.delete('dialogStatus');
		searchParams.delete('dialogToEntity');
		setSearchParams(searchParams);
	};

	const handleOpenDialog = (dialog: string) => {
		searchParams.set('dialog', dialog);
		searchParams.set('dialogInvoice', String(invoice.id));
		setSearchParams(searchParams);
	};

	// tabs
	const tabs: TabProps[] = [
		{
			label: t('payments'),
			value: 'payments',
		},
	];

	if (userProjectPermissions?.document?.read) {
		tabs.push({
			label: t('documents'),
			value: 'documents',
		});
	}

	// menu items
	const btnMenuItems: MenuItemProps[] = [];

	if (invoice.status === 'PENDING_REVIEW' && userProjectPermissions?.invoice?.approve) {
		btnMenuItems.push({
			children: t('rejectInvoice'),
			icon: <IconClose />,
			onClick: () => {
				handleOpenDialog('rejectInvoice');
			},
		});

		btnMenuItems.push({
			children: t('approveInvoice'),
			icon: <IconCheck />,
			onClick: () => {
				handleOpenDialog('approveInvoice');
			},
		});
	}

	const contractId =
		typeof invoice.contract === 'object' && invoice.contract
			? (invoice.contract as Contract).id
			: invoice.contract;

	const supplier = invoice.supplier as Entity;

	if (invoice.status === 'READY_FOR_PAYMENT' && userProjectPermissions?.payment?.create) {
		btnMenuItems.push({
			children: t('recordPayment'),
			icon: <IconPaid />,
			onClick: () => {
				searchParams.set('dialog', 'recordPayment');
				searchParams.set('dialogInvoice', String(invoice.id));

				if (contractId) {
					searchParams.set('dialogContract', String(contractId));
				}

				if (supplier) {
					searchParams.set('dialogToEntity', String(supplier?.id));
				}

				setSearchParams(searchParams);
			},
		});

		if (!user?.isWbg) {
			btnMenuItems.push({
				children: t('initiateDirectPayment'),
				icon: <IconPaid />,
				onClick: () => {
					searchParams.set('dialog', 'initiateDirectPayment');
					searchParams.set('dialogInvoice', String(invoice.id));
					searchParams.set('dialogStatus', 'INITIATED');

					if (contractId) {
						searchParams.set('dialogContract', String(contractId));
					}

					if (supplier) {
						searchParams.set('dialogToEntity', String(supplier?.id));
					}

					setSearchParams(searchParams);
				},
			});
		}
	}

	if (invoice.status === 'REJECTED' && userProjectPermissions?.invoice?.approve) {
		btnMenuItems.push({
			children: t('undoRejection'),
			icon: <IconUndo />,
			onClick: () => {
				handleOpenDialog('resetInvoice');
			},
		});
	}

	if (userProjectPermissions?.document?.create) {
		btnMenuItems.push({
			children: t('uploadDocuments'),
			icon: <IconInsertDriveFile />,
			onClick: () => {
				handleOpenDialog('uploadDocsInvoice');
			},
		});
	}

	// dialog status
	let invoiceDialogStatus: DialogUpdateInvoiceStatusPropsType['status'] | undefined;

	if (paramDialog === 'approveInvoice') {
		invoiceDialogStatus = 'READY_FOR_PAYMENT';
	} else if (paramDialog === 'rejectInvoice') {
		invoiceDialogStatus = 'REJECTED';
	} else if (paramDialog === 'resetInvoice') {
		invoiceDialogStatus = 'PENDING_REVIEW';
	}

	const dialogUpdateInvoiceStatusProps: Partial<DialogUpdateInvoiceStatusPropsType> = {
		invoice,
		isOpen: Boolean(invoiceDialogStatus) && paramDialogInvoice === String(invoice.id),
		onClose: handleCloseDialog,
		onSuccess: () => {
			if (refetchInvoices) {
				refetchInvoices();
			}
		},
	};

	if (invoiceDialogStatus) {
		dialogUpdateInvoiceStatusProps.status = invoiceDialogStatus;
	}

	const DialogAddTransactionProps: DialogAddTransactionProps = {
		isOpen: paramDialog === 'recordPayment' && paramDialogInvoice === String(invoice.id),
		onClose: handleCloseDialog,
		onSuccess: () => {
			if (refetchInvoices) {
				refetchInvoices();
			}
		},
		type: 'PAYMENT',
	};

	if (paramDialog === 'initiateDirectPayment') {
		DialogAddTransactionProps.isOpen =
			paramDialog === 'initiateDirectPayment' && paramDialogInvoice === String(invoice.id);
		DialogAddTransactionProps.type = 'DIRECT';
	}

	return (
		<>
			<Paper
				sx={(theme) => {
					const borderColor = isExpanded ? theme.palette.brand.blue[500] : 'transparent';
					return {
						border: `1px ${borderColor} solid`,
						position: 'relative',
					};
				}}>
				<Box
					sx={(theme) => {
						return {
							padding: theme.spacing(2),
						};
					}}>
					<Grid container spacing={2} alignItems="center">
						<Grid item xs={12} lg={2}>
							<Grid alignItems="center" container spacing={2} wrap="nowrap">
								<Grid item>
									<IconCircle TooltipProps={{ title: t('invoice') }} type="invoice" />
								</Grid>
								<Grid item>
									<TypographyCaption id={invoice.id} />
									<Typography fontWeight={600}>{invoice.num || t('na')}</Typography>
								</Grid>
							</Grid>
						</Grid>
						<Grid item xs={12} lg={1.5}>
							<RowKeyValue
								label={t('supplier')}
								value={<LinkEntity entity={invoice.supplier as Entity} />}
							/>
						</Grid>
						<Grid item xs={12} lg={1.5}>
							<RowKeyValue label={t('status')} value={<ChipStatus status={invoice.status} />} />
						</Grid>
						<Grid item xs={12} lg={1.5}>
							<RowKeyValue
								label={t('contract')}
								value={<Typography>{contractNumber}</Typography>}
							/>
						</Grid>
						<Grid item xs={12} lg={1}>
							<RowKeyValue
								label={t('amount')}
								value={
									<Amount
										currency={invoice.currency}
										TypographyProps={{ fontWeight: 400 }}
										value={invoice.amount}
									/>
								}
							/>
						</Grid>
						<Grid item xs={12} lg={1}>
							<RowKeyValue
								label={t('tax')}
								value={
									<Amount
										conversionField="tax"
										currency={invoice.currency}
										TypographyProps={{ fontWeight: 400 }}
										value={invoice.tax ?? 0}
									/>
								}
							/>
						</Grid>
						<Grid item xs={12} lg={1}>
							<RowKeyValue
								label={t('total')}
								value={<Amount currency={invoice.currency} value={total} />}
							/>
						</Grid>
						<Grid item xs={12} lg={1.5}>
							<RowKeyValue
								label={t('updated')}
								value={
									<UserAvatarModified
										activityTypes={['INVOICE_UPDATED', 'INVOICE_APPROVED', 'INVOICE_CREATED']}
										date={invoice.modified || invoice.created}
										invoice={invoice}
									/>
								}
							/>
						</Grid>
						<Grid item xs={12} lg={true}>
							<Grid
								alignItems="center"
								container
								justifyContent={{ xs: 'space-between', lg: 'flex-end' }}
								spacing={1}>
								{btnMenuItems.length > 0 && (
									<Grid
										item
										sx={{
											position: 'absolute',
											top: 0,
											right: theme.spacing(1),
											[theme.breakpoints.up('lg')]: {
												position: 'relative',
												top: 'auto',
												right: 'auto',
											},
										}}>
										<BtnMenu items={btnMenuItems} />
									</Grid>
								)}
								<Grid item>
									<BtnExpand
										isExpanded={isExpanded}
										label={isExpanded ? t('hideDetails') : t('seeDetails')}
										onClick={handleToggleCard}
									/>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</Box>
				{isExpanded && (
					<TabContext value={tabValue}>
						<Tabs
							BoxProps={{
								sx: {
									marginTop: {
										lg: theme.spacing(3),
									},
								},
							}}
							hasBorder={false}
							onChange={handleChangeTab}
							tabs={tabs}
							value={tabValue}
						/>
						<TabPanel value="payments">
							<TableBasicTransactions
								data={transactions}
								headersIncluded={[
									'amount',
									'date',
									'recorded',
									'status',
									'tax',
									'total',
									'type',
									'updated',
								]}
								isLoading={isLoadingTransactions}
							/>
						</TabPanel>
						<TabPanel value="documents">
							<TableBasicDocs
								data={documents}
								isLoading={isLoadingDocs}
								pagination={paginationDocuments}
								refetchDocuments={refetchDocuments}
							/>
						</TabPanel>
					</TabContext>
				)}
			</Paper>
			{dialogUpdateInvoiceStatusProps.status && (
				<DialogUpdateInvoiceStatus
					{...(dialogUpdateInvoiceStatusProps as DialogUpdateInvoiceStatusPropsType)}
				/>
			)}
			<DialogAddTransaction {...DialogAddTransactionProps} />
			<DialogUploadDocs
				invoice={invoice}
				isOpen={paramDialog === 'uploadDocsInvoice' && paramDialogInvoice === String(invoice.id)}
				onClose={handleCloseDialog}
				onSuccess={() => {
					if (refetchInvoices) {
						refetchInvoices();
					}
				}}
			/>
		</>
	);
};

export interface CardInvoiceProps {
	invoice: Invoice;
	refetchInvoices?: () => void;
}
